import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { useSiteSettings } from "../../contexts/SiteSettingsContext";
import { toast } from "sonner";

const TIERS = ["none", "starter", "pro", "scale", "whale", "titan"];
const STATUSES = ["pending", "active", "suspended"];
const ROLES = ["user", "admin"];

const OVERRIDE_FIELDS = [
    { key: "ad_request_fee_percent_override", label: "Fee %", global: "topup_fee_percent" },
    { key: "fee_override_whiteHat", label: "White-hat fee %", global: "__tier_whiteHat" },
    { key: "fee_override_grayHat", label: "Gray-hat fee %", global: "__tier_grayHat" },
    { key: "fee_override_casino", label: "Casino fee %", global: "__tier_casino" },
    { key: "min_topup_override_meta_standard", label: "Meta Standard Min", global: "min_topup_meta_standard" },
    { key: "min_topup_override_meta_flex", label: "Meta Flex Min", global: "min_topup_meta_flex" },
    { key: "min_topup_override_google", label: "Google Min", global: "min_topup_google" },
    { key: "min_topup_override_tiktok", label: "TikTok Min", global: "min_topup_tiktok" },
    { key: "min_topup_override_snapchat", label: "Snapchat Min", global: "min_topup_snapchat" },
    { key: "min_topup_override_bing", label: "Bing Min", global: "min_topup_bing" },
    { key: "min_topup_override_taboola", label: "Taboola Min", global: "min_topup_taboola" },
    { key: "min_topup_override_outbrain", label: "Outbrain Min", global: "min_topup_outbrain" },
];

const TIER_DEFAULT = { whiteHat: 4, grayHat: 7, casino: 10 };

function buildPolicyDiff(user, settings) {
    return OVERRIDE_FIELDS
        .filter((f) => user?.[f.key] !== null && user?.[f.key] !== undefined && user?.[f.key] !== "")
        .map((f) => ({
            label: f.label,
            overrideValue: Number(user?.[f.key]),
            globalValue: f.global.startsWith("__tier_") ? TIER_DEFAULT[f.global.slice(7)] : Number(settings?.[f.global] ?? 0),
            key: f.key,
        }));
}

function BalanceAdjustDialog({ user, onDone }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({ amount: "", note: "" });

    const submit = async (e) => {
        e.preventDefault();
        const amount = Number(form.amount);
        if (!amount || Number.isNaN(amount)) {
            toast.error("Geçerli bir tutar girin (+/-)");
            return;
        }
        setLoading(true);
        try {
            await api.post("/admin/transactions/adjust-balance", {
                user_id: user.id,
                amount,
                note: form.note || undefined,
            });
            toast.success(`Bakiye güncellendi (${amount > 0 ? "+" : ""}${amount.toFixed(2)} USD)`);
            setOpen(false);
            setForm({ amount: "", note: "" });
            onDone();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Bakiye güncelleme başarısız");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button
                    size="sm"
                    variant="outline"
                    data-testid={`user-balance-adjust-open-${user.id}`}
                    className="border-primary/40 text-primary hover:bg-primary/10"
                >
                    Bakiye +/-
                </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-white/10 max-w-md">
                <DialogHeader>
                    <DialogTitle>Bakiye ayarla</DialogTitle>
                    <DialogDescription>
                        Pozitif değer ekler, negatif değer düşer. İşlem kayıt altına alınır.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3" data-testid="user-balance-adjust-form">
                    <div className="rounded-xl border border-white/10 bg-background/40 p-3 text-xs space-y-1">
                        <p><strong>Kullanıcı:</strong> {user.full_name}</p>
                        <p className="font-mono text-muted-foreground">{user.email}</p>
                        <p data-testid={`user-current-balance-${user.id}`}><strong>Mevcut:</strong> ${Number(user.usd_balance || 0).toFixed(2)}</p>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Tutar (USD)</Label>
                        <Input
                            type="number"
                            step="0.01"
                            value={form.amount}
                            onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                            data-testid="user-balance-adjust-amount"
                            placeholder="Örn: 50 veya -50"
                            className="bg-background border-white/10"
                            required
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label>Not</Label>
                        <Textarea
                            rows={2}
                            value={form.note}
                            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                            data-testid="user-balance-adjust-note"
                            className="bg-background border-white/10"
                            placeholder="Opsiyonel"
                        />
                    </div>
                    <Button
                        type="submit"
                        disabled={loading}
                        data-testid="user-balance-adjust-submit"
                        className="w-full bg-primary text-primary-foreground"
                    >
                        {loading ? "Kaydediliyor..." : "Bakiyeyi güncelle"}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function PricingOverrideDialog({ user, settings, onDone }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        ad_request_fee_percent_override: user.ad_request_fee_percent_override ?? "",
        min_topup_override_meta_standard: user.min_topup_override_meta_standard ?? "",
        min_topup_override_meta_flex: user.min_topup_override_meta_flex ?? "",
        min_topup_override_google: user.min_topup_override_google ?? "",
        min_topup_override_tiktok: user.min_topup_override_tiktok ?? "",
        min_topup_override_snapchat: user.min_topup_override_snapchat ?? "",
        min_topup_override_bing: user.min_topup_override_bing ?? "",
        min_topup_override_taboola: user.min_topup_override_taboola ?? "",
        min_topup_override_outbrain: user.min_topup_override_outbrain ?? "",
    });

    const toMaybeNumber = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
    const policyDiff = useMemo(() => buildPolicyDiff(user, settings), [user, settings]);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {};
            Object.entries(form).forEach(([k, v]) => {
                payload[k] = toMaybeNumber(v);
            });
            await api.patch(`/admin/users/${user.id}`, payload);
            toast.success("Kullanıcı override ayarları güncellendi");
            setOpen(false);
            onDone();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Override güncellenemedi");
        } finally {
            setLoading(false);
        }
    };

    const numInput = (key, label, testid) => (
        <div className="space-y-1" key={key}>
            <Label>{label}</Label>
            <Input
                type="number"
                step="0.01"
                value={form[key]}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                data-testid={testid}
                className="bg-background border-white/10"
                placeholder="boş = global ayar"
            />
        </div>
    );

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button
                    size="sm"
                    variant="outline"
                    data-testid={`user-pricing-override-open-${user.id}`}
                    className="border-white/20 hover:bg-white/10"
                >
                    Override
                </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-white/10 max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Kullanıcıya özel fiyat/min top-up override</DialogTitle>
                    <DialogDescription>Boş bırakılırsa global ayar kullanılır.</DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3" data-testid="user-pricing-override-form">
                    <div className="rounded-xl border border-white/10 bg-background/40 p-3" data-testid="policy-diff-dialog-card">
                        <p className="text-xs font-semibold mb-2">Policy Diff (Global → User Override)</p>
                        {policyDiff.length === 0 ? (
                            <p className="text-xs text-muted-foreground">Override yok, global politika geçerli.</p>
                        ) : (
                            <div className="grid sm:grid-cols-2 gap-2">
                                {policyDiff.map((d) => (
                                    <div key={d.key} className="text-xs border border-white/10 rounded-lg px-2.5 py-1.5" data-testid={`policy-diff-item-${d.key}`}>
                                        <p className="text-muted-foreground">{d.label}</p>
                                        <p className="font-mono">{d.globalValue} → <span className="text-primary">{d.overrideValue}</span></p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                        {numInput("ad_request_fee_percent_override", "Fee override (%)", "override-fee")}
                        {numInput("min_topup_override_meta_standard", "Meta Standard min", "override-meta-standard")}
                        {numInput("min_topup_override_meta_flex", "Meta Flex min", "override-meta-flex")}
                        {numInput("min_topup_override_google", "Google min", "override-google")}
                        {numInput("min_topup_override_tiktok", "TikTok min", "override-tiktok")}
                        {numInput("min_topup_override_snapchat", "Snapchat min", "override-snapchat")}
                        {numInput("min_topup_override_bing", "Bing min", "override-bing")}
                        {numInput("min_topup_override_taboola", "Taboola min", "override-taboola")}
                        {numInput("min_topup_override_outbrain", "Outbrain min", "override-outbrain")}
                    </div>
                    <Button type="submit" disabled={loading} data-testid="user-pricing-override-submit" className="w-full bg-primary text-primary-foreground">
                        {loading ? "Kaydediliyor..." : "Override kaydet"}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default function AdminUsers() {
    const [users, setUsers] = useState([]);
    const [q, setQ] = useState("");
    const { settings } = useSiteSettings();

    const load = async (query = q) => {
        try {
            const { data } = await api.get(`/admin/users?limit=150${query ? `&q=${encodeURIComponent(query)}` : ""}`);
            setUsers(data);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Kullanıcılar alınamadı");
        }
    };
    useEffect(() => { load(); }, []);

    useEffect(() => {
        const id = setTimeout(() => {
            load(q);
        }, 350);
        return () => clearTimeout(id);
    }, [q]);

    const update = async (id, payload) => {
        try {
            await api.patch(`/admin/users/${id}`, payload);
            toast.success("User updated");
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed");
        }
    };

    return (
        <div data-testid="admin-users" className="space-y-6">
            <div>
                <h1 className="font-display font-bold text-3xl tracking-tight">Users</h1>
                <p className="text-muted-foreground mt-1">
                    Güncel bakiye, rol/durum ve bakiye +/- işlemlerini tek yerden yönetin.
                </p>
            </div>

            <div className="flex gap-2">
                <Input
                    placeholder="Search email / name / company"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            load(q);
                        }
                    }}
                    data-testid="users-search"
                    className="bg-card border-white/10"
                />
                <Button onClick={() => load(q)} data-testid="users-search-button">
                    Search
                </Button>
            </div>
            <p className="text-xs text-muted-foreground -mt-4" data-testid="users-search-help">
                İsim, email ve şirket adına göre arar. Yazdıkça otomatik filtrelenir, Search ile manuel de tetikleyebilirsiniz.
            </p>

            <div className="bg-card border border-white/[0.07] rounded-2xl overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-white/[0.02] text-xs font-mono uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="text-left p-4">User</th>
                            <th className="text-right p-4">USD Balance</th>
                            <th className="text-left p-4 hidden md:table-cell">Status</th>
                            <th className="text-left p-4 hidden md:table-cell">Role</th>
                            <th className="text-left p-4 hidden lg:table-cell">Tier override</th>
                            <th className="p-4"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                        {users.map((u) => (
                            <tr key={u.id} data-testid={`user-row-${u.id}`}>
                                <td className="p-4">
                                    <Link to={`/admin/users/${u.id}`} className="font-medium hover:text-primary" data-testid={`user-name-link-${u.id}`}>{u.full_name}</Link>
                                    <p className="text-xs text-muted-foreground font-mono">
                                        {u.email}
                                    </p>
                                    {buildPolicyDiff(u, settings).length > 0 && (
                                        <p
                                            className="inline-flex mt-1 text-[10px] px-2 py-0.5 rounded-md bg-primary/15 text-primary border border-primary/30"
                                            data-testid={`policy-diff-row-badge-${u.id}`}
                                        >
                                            Policy Diff: {buildPolicyDiff(u, settings).length}
                                        </p>
                                    )}
                                </td>
                                <td className="p-4 text-right">
                                    <p
                                        data-testid={`user-balance-${u.id}`}
                                        className={`font-semibold ${Number(u.usd_balance || 0) < 0 ? "text-red-400" : "text-emerald-400"}`}
                                    >
                                        ${Number(u.usd_balance || 0).toFixed(2)}
                                    </p>
                                    <p className="text-[11px] text-muted-foreground">cashback: ${Number(u.cashback_balance || 0).toFixed(2)}</p>
                                </td>
                                <td className="p-4 hidden md:table-cell">
                                    <select
                                        value={u.status}
                                        onChange={(e) => update(u.id, { status: e.target.value })}
                                        data-testid={`user-status-${u.id}`}
                                        className="bg-background border border-white/10 rounded-lg px-2 py-1 text-xs"
                                    >
                                        {STATUSES.map((s) => <option key={s}>{s}</option>)}
                                    </select>
                                </td>
                                <td className="p-4 hidden md:table-cell">
                                    <select
                                        value={u.role}
                                        onChange={(e) => update(u.id, { role: e.target.value })}
                                        data-testid={`user-role-${u.id}`}
                                        className="bg-background border border-white/10 rounded-lg px-2 py-1 text-xs"
                                    >
                                        {ROLES.map((r) => <option key={r}>{r}</option>)}
                                    </select>
                                </td>
                                <td className="p-4 hidden lg:table-cell">
                                    <select
                                        value={u.tier_override || "none"}
                                        onChange={(e) => update(u.id, { tier_override: e.target.value })}
                                        data-testid={`user-tier-${u.id}`}
                                        className="bg-background border border-white/10 rounded-lg px-2 py-1 text-xs"
                                    >
                                        {TIERS.map((t) => <option key={t}>{t}</option>)}
                                    </select>
                                </td>
                                <td className="p-4 text-right text-xs text-muted-foreground font-mono">
                                    <div className="flex items-center justify-end gap-2">
                                        <Link
                                            to={`/admin/users/${u.id}`}
                                            data-testid={`user-detail-link-${u.id}`}
                                            className="inline-flex items-center gap-1 h-8 px-3 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90"
                                        >
                                            Detay <ChevronRight className="w-3.5 h-3.5" />
                                        </Link>
                                        <PricingOverrideDialog user={u} settings={settings} onDone={load} />
                                        <BalanceAdjustDialog user={u} onDone={load} />
                                        <span>{new Date(u.created_at).toLocaleDateString()}</span>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {users.length === 0 && (
                    <p className="text-sm text-muted-foreground p-12 text-center">No users.</p>
                )}
            </div>
        </div>
    );
}
